/**
 * Supabase Query Helper Utilities
 * Provides optimized query patterns with pagination, filtering, and sorting
 */

import { SupabaseClient } from '@supabase/supabase-js';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface PaginationParams {
  page: number;
  pageSize: number;
}

export interface SortParams {
  column: string;
  ascending?: boolean;
}

export interface FilterParams {
  [key: string]: any;
}

export interface QueryResult<T> {
  data: T[];
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface QueryPerformanceMetrics {
  queryCount: number;
  totalExecutionTime: number;
  queries: Array<{
    table: string;
    operation: string;
    executionTime: number;
    timestamp: string;
  }>;
}

// ============================================================================
// PERFORMANCE MONITORING
// ============================================================================

class QueryPerformanceMonitor {
  private metrics: QueryPerformanceMetrics = {
    queryCount: 0,
    totalExecutionTime: 0,
    queries: [],
  };

  private isDevMode = typeof window !== 'undefined' ? process.env.NODE_ENV === 'development' : false;

  startQuery(table: string, operation: string): () => void {
    if (!this.isDevMode) return () => {};

    const startTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
    return () => {
      const executionTime = typeof performance !== 'undefined' ? performance.now() - startTime : Date.now() - startTime;
      this.metrics.queryCount++;
      this.metrics.totalExecutionTime += executionTime;
      this.metrics.queries.push({
        table,
        operation,
        executionTime: Math.round(executionTime * 100) / 100,
        timestamp: new Date().toISOString(),
      });

      // Log slow queries (> 500ms)
      if (executionTime > 500) {
        console.warn(
          `[SLOW QUERY] ${table}.${operation} took ${executionTime.toFixed(2)}ms`
        );
      }
    };
  }

  getMetrics(): QueryPerformanceMetrics {
    return { ...this.metrics };
  }

  reset(): void {
    this.metrics = {
      queryCount: 0,
      totalExecutionTime: 0,
      queries: [],
    };
  }

  logSummary(): void {
    if (!this.isDevMode || this.metrics.queryCount === 0) return;

    console.group('📊 Query Performance Summary');
    console.log(`Total Queries: ${this.metrics.queryCount}`);
    console.log(
      `Total Execution Time: ${this.metrics.totalExecutionTime.toFixed(2)}ms`
    );
    console.log(
      `Average Query Time: ${(
        this.metrics.totalExecutionTime / this.metrics.queryCount
      ).toFixed(2)}ms`
    );
    console.table(
      this.metrics.queries.map((q) => ({
        Table: q.table,
        Operation: q.operation,
        'Time (ms)': q.executionTime,
      }))
    );
    console.groupEnd();
  }
}

export const queryMonitor = new QueryPerformanceMonitor();

// ============================================================================
// PAGINATED QUERY BUILDER
// ============================================================================

export async function paginatedQuery<T>(
  supabase: SupabaseClient,
  table: string,
  options: {
    organizationId: string;
    pagination: PaginationParams;
    sort?: SortParams;
    filters?: FilterParams;
    select?: string;
  }
): Promise<QueryResult<T>> {
  const endQuery = queryMonitor.startQuery(table, 'paginated_query');

  try {
    const { organizationId, pagination, sort, filters, select } = options;
    const { page, pageSize } = pagination;

    // Calculate offset
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    // Build base query with organization filter
    let query = supabase
      .from(table)
      .select(select || '*', { count: 'exact' })
      .eq('organization_id', organizationId);

    // Apply additional filters
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            query = query.in(key, value);
          } else if (typeof value === 'string' && value.includes('%')) {
            query = query.ilike(key, value);
          } else {
            query = query.eq(key, value);
          }
        }
      });
    }

    // Apply sorting
    if (sort) {
      query = query.order(sort.column, { ascending: sort.ascending ?? false });
    }

    // Apply pagination
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) throw error;

    const totalPages = count ? Math.ceil(count / pageSize) : 0;

    return {
      data: (data as T[]) || [],
      count: count || 0,
      page,
      pageSize,
      totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1,
    };
  } finally {
    endQuery();
  }
}

// ============================================================================
// OPTIMIZED QUERY PATTERNS
// ============================================================================

/**
 * Fetch use cases with optimized column selection
 */
export async function fetchUseCases(
  supabase: SupabaseClient,
  organizationId: string,
  options?: {
    pagination?: PaginationParams;
    filters?: {
      status?: string;
      department?: string;
      riskTier?: string;
      search?: string;
    };
  }
) {
  const endQuery = queryMonitor.startQuery('ai_use_cases', 'fetch');

  try {
    const pagination = options?.pagination || { page: 1, pageSize: 50 };
    const filters: FilterParams = {};

    if (options?.filters?.status) filters.status = options.filters.status;
    if (options?.filters?.department)
      filters.department = options.filters.department;
    if (options?.filters?.riskTier)
      filters.risk_tier = options.filters.riskTier;

    return await paginatedQuery(supabase, 'ai_use_cases', {
      organizationId,
      pagination,
      filters,
      sort: { column: 'created_at', ascending: false },
      select:
        'id, name, description, business_function, department, status, risk_tier, risk_assessment_status, created_at, updated_at',
    });
  } finally {
    endQuery();
  }
}

/**
 * Fetch risk assessments with use case details (optimized join)
 */
export async function fetchRiskAssessmentsWithUseCases(
  supabase: SupabaseClient,
  organizationId: string,
  options?: {
    pagination?: PaginationParams;
    filters?: {
      workflowState?: string;
      riskTier?: string;
    };
  }
) {
  const endQuery = queryMonitor.startQuery(
    'risk_assessments',
    'fetch_with_use_cases'
  );

  try {
    const pagination = options?.pagination || { page: 1, pageSize: 50 };
    const filters: FilterParams = {};

    if (options?.filters?.workflowState)
      filters.workflow_state = options.filters.workflowState;
    if (options?.filters?.riskTier)
      filters.risk_tier = options.filters.riskTier;

    return await paginatedQuery(supabase, 'risk_assessments', {
      organizationId,
      pagination,
      filters,
      sort: { column: 'created_at', ascending: false },
      select:
        'id, use_case_id, risk_tier, workflow_state, likelihood_score, impact_score, overall_risk_score, created_at, ai_use_cases(id, name, department)',
    });
  } finally {
    endQuery();
  }
}

/**
 * Fetch audit logs with user details (optimized join)
 */
export async function fetchAuditLogs(
  supabase: SupabaseClient,
  organizationId: string,
  options?: {
    pagination?: PaginationParams;
    filters?: {
      entityType?: string;
      action?: string;
      userId?: string;
      dateFrom?: string;
      dateTo?: string;
    };
  }
) {
  const endQuery = queryMonitor.startQuery('audit_logs', 'fetch');

  try {
    const pagination = options?.pagination || { page: 1, pageSize: 100 };
    const filters: FilterParams = {};

    if (options?.filters?.entityType)
      filters.entity_type = options.filters.entityType;
    if (options?.filters?.action) filters.action = options.filters.action;
    if (options?.filters?.userId) filters.user_id = options.filters.userId;

    return await paginatedQuery(supabase, 'audit_logs', {
      organizationId,
      pagination,
      filters,
      sort: { column: 'created_at', ascending: false },
      select:
        'id, entity_type, entity_id, action, title, description, user_id, created_at, user_profiles(full_name, email)',
    });
  } finally {
    endQuery();
  }
}

/**
 * Fetch escalation events (unresolved only)
 */
export async function fetchUnresolvedEscalations(
  supabase: SupabaseClient,
  organizationId: string,
  options?: {
    pagination?: PaginationParams;
  }
) {
  const endQuery = queryMonitor.startQuery(
    'escalation_events',
    'fetch_unresolved'
  );

  try {
    const pagination = options?.pagination || { page: 1, pageSize: 50 };

    return await paginatedQuery(supabase, 'escalation_events', {
      organizationId,
      pagination,
      filters: { status: 'open' },
      sort: { column: 'created_at', ascending: false },
      select:
        'id, use_case_id, risk_assessment_id, severity, status, escalation_reason, created_at, ai_use_cases(name, department)',
    });
  } finally {
    endQuery();
  }
}

/**
 * Fetch notifications (unread only)
 */
export async function fetchUnreadNotifications(
  supabase: SupabaseClient,
  userId: string,
  organizationId: string,
  options?: {
    pagination?: PaginationParams;
  }
) {
  const endQuery = queryMonitor.startQuery('notifications', 'fetch_unread');

  try {
    const pagination = options?.pagination || { page: 1, pageSize: 20 };
    const { page, pageSize } = pagination;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const { data, error, count } = await supabase
      .from('notifications')
      .select('id, title, message, type, is_read, created_at', {
        count: 'exact',
      })
      .eq('user_id', userId)
      .eq('organization_id', organizationId)
      .eq('is_read', false)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) throw error;

    const totalPages = count ? Math.ceil(count / pageSize) : 0;

    return {
      data: data || [],
      count: count || 0,
      page,
      pageSize,
      totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1,
    };
  } finally {
    endQuery();
  }
}

/**
 * Fetch controls with assignment status (optimized join)
 */
export async function fetchControlsWithStatus(
  supabase: SupabaseClient,
  organizationId: string,
  useCaseId?: string,
  options?: {
    pagination?: PaginationParams;
  }
) {
  const endQuery = queryMonitor.startQuery(
    'controls_library',
    'fetch_with_status'
  );

  try {
    const pagination = options?.pagination || { page: 1, pageSize: 50 };
    const { page, pageSize } = pagination;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('controls_library')
      .select(
        `
        id,
        control_id,
        control_name,
        description,
        category,
        framework_references,
        control_status!left(
          id,
          status,
          implementation_notes,
          evidence_url
        )
      `,
        { count: 'exact' }
      )
      .order('control_id', { ascending: true })
      .range(from, to);

    if (useCaseId) {
      query = query.eq('control_status.use_case_id', useCaseId);
    }

    const { data, error, count } = await query;

    if (error) throw error;

    const totalPages = count ? Math.ceil(count / pageSize) : 0;

    return {
      data: data || [],
      count: count || 0,
      page,
      pageSize,
      totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1,
    };
  } finally {
    endQuery();
  }
}

/**
 * Fetch dashboard summary with Redis caching
 */
export async function fetchDashboardSummary(
  supabase: SupabaseClient,
  organizationId: string
) {
  const endQuery = queryMonitor.startQuery('dashboard', 'summary');

  try {
    // Try cache first
    const cacheKey = CacheKeys.dashboardSummary(organizationId);
    const cached = await cacheGet<any>(cacheKey);
    if (cached) {
      console.log('[Cache HIT] Dashboard summary');
      return cached;
    }
    console.log('[Cache MISS] Dashboard summary');

    // Fetch from database
    const [useCasesResult, assessmentsResult, escalationsResult] = await Promise.all([
      supabase
        .from('ai_use_cases')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', organizationId),
      supabase
        .from('risk_assessments')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', organizationId)
        .in('workflow_state', ['pending_review', 'in_progress']),
      supabase
        .from('escalation_events')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', organizationId)
        .eq('resolution_status', 'unresolved'),
    ]);

    const summary = {
      totalUseCases: useCasesResult.count || 0,
      totalAssessments: assessmentsResult.count || 0,
      unresolvedEscalations: escalationsResult.count || 0,
    };

    // Cache for 1 hour
    await cacheSet(cacheKey, summary, CacheTTL.DASHBOARD_QUERIES);

    return summary;
  } finally {
    endQuery();
  }
}

/**
 * Invalidate dashboard cache on data mutation
 */
export async function invalidateDashboardCache(organizationId: string): Promise<void> {
  await cacheDelete(CacheKeys.dashboardSummary(organizationId));
  await cacheDelete(CacheKeys.cmriScore(organizationId));
  await cacheDelete(CacheKeys.riskDistribution(organizationId));
}

// ============================================================================
// BATCH OPERATIONS
// ============================================================================

/**
 * Batch update records (optimized for bulk operations)
 */
export async function batchUpdate<T extends { id: string }>(
  supabase: SupabaseClient,
  table: string,
  records: Partial<T>[],
  organizationId: string
) {
  const endQuery = queryMonitor.startQuery(table, 'batch_update');

  try {
    const updates = records.map((record) =>
      supabase
        .from(table)
        .update(record)
        .eq('id', record.id!)
        .eq('organization_id', organizationId)
    );

    const results = await Promise.all(updates);
    const errors = results.filter((r) => r.error);

    if (errors.length > 0) {
      throw new Error(
        `Batch update failed: ${errors.map((e) => e.error?.message).join(', ')}`
      );
    }

    return { success: true, updatedCount: records.length };
  } finally {
    endQuery();
  }
}

// ============================================================================
// EXPORT UTILITIES
// ============================================================================

export { QueryPerformanceMonitor };